import { toast } from 'react-toastify';

import { ID, Response } from 'app/components/Table/core/types';

import { RACK_HAS_BEEN_SYNCED_MSG } from 'app/modules/inventory/racks/core/constants';
import { ApiResponse } from 'app/modules/mail-history/core/types';
import { Sensor } from 'app/modules/system-settings/devices/core/type';

import { getTimezoneOffset, trimObject } from 'app/helpers';
import { getResponseError } from 'app/helpers/errors';

import { API_URL } from 'app/constants/environments';

import { HistoryBin, HistoryBinQueryResponse } from '../../bins/core/type';
import { StatusStockUp } from '../../stock-run/core/enum';
import { StockRun, StockRunsQueryResponse } from '../../stock-run/core/type';
import {
  ActivityLog,
  ActivityLogDetailQueryResponse,
  LogItemDetail,
  Rack,
  RackRequest,
  RacksActivityLogResponse,
  RacksQueryResponse,
  UpdateBinsRequest,
} from './type';
import axios, { AxiosResponse } from 'axios';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const RACK_URL = `${API_URL}/api`;
const RACK_URL_V2 = `${API_URL}/api`;
const getRacks = (query: string): Promise<RacksQueryResponse> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios.get(`${RACK_URL}/orgs/${organizationId}/racks?${query}`).then(
    (
      d: AxiosResponse<{
        data: {
          items: Array<Rack>;
          total: number;
        };
      }>,
    ) => {
      return {
        data: d.data.data,
      };
    },
  );
};
const getRacks2 = (query: string): Promise<RacksQueryResponse> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios.get(`${RACK_URL_V2}/orgs/${organizationId}/racks-v2?${query}`).then(
    (
      d: AxiosResponse<{
        data: {
          items: Array<Rack>;
          total: number;
        };
      }>,
    ) => {
      return {
        data: d.data.data,
      };
    },
  );
};
const getHistory = (query: string, rackId: ID): Promise<HistoryBinQueryResponse> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios.get(`${RACK_URL}/orgs/${organizationId}/racks/${rackId}/history?${query}`).then(
    (
      d: AxiosResponse<{
        data: {
          items: Array<HistoryBin>;
          total: number;
        };
      }>,
    ) => {
      return {
        data: d.data.data,
      };
    },
  );
};

const getRack = (rackId: ID, organizationId: number | undefined): Promise<Rack | undefined> => {
  return axios
    .get(`${RACK_URL}/orgs/${organizationId}/racks/${rackId}`)
    .then((response: AxiosResponse<Response<Rack>>) => response.data)
    .then((response: Response<Rack>) => response.data);
};

const createRack = (request: RackRequest): Promise<Rack | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .post(`${RACK_URL}/orgs/${organizationId}/racks`, trimObject(request))
    .then((response: AxiosResponse<Response<Rack>>) => response.data)
    .then((response: Response<Rack>) => response.data);
};
const deleteRack = (rackId: ID): Promise<void> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios.delete(`${RACK_URL}/orgs/${organizationId}/racks/${rackId}`);
};

const updateRack = (rackId: ID, request: RackRequest): Promise<Rack | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .patch(`${RACK_URL}/orgs/${organizationId}/racks/${rackId}`, trimObject(request))
    .then((response: AxiosResponse<Response<Rack>>) => response.data)
    .then((response: Response<Rack>) => response.data);
};

const updateBinInRack = (rackId: ID, request: UpdateBinsRequest[]) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .post(`${RACK_URL}/orgs/${organizationId}/racks/${rackId}/items`, { itemToBins: request })
    .then((response) => response.data)
    .catch((error) => {
      console.log('error', error);
      getResponseError(error);
      return { success: false, error: error?.response?.data?.message || 'Failed to update bins' };
    });
};

const synchronizedRack = async (
  rackId: ID,
  request: {
    isSync: boolean;
  },
): Promise<Rack | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  try {
    const axiosResponse: AxiosResponse<Response<Rack>> = await axios.patch(
      `${RACK_URL}/orgs/${organizationId}/racks/${rackId}/synchronized`,
      request,
    );

    const apiResponse: Response<Rack> = axiosResponse.data;
    return apiResponse.data;
  } catch (error: any) {
    const errorMessage = error?.response?.data?.data?.errors;
    errorMessage === RACK_HAS_BEEN_SYNCED_MSG
      ? toast.success('Rack has been synced')
      : getResponseError(error);
    return undefined;
  }
};

export const getRackActivityLogs = (rackId: ID): Promise<ActivityLog | undefined> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .get(`${RACK_URL}/orgs/${organizationId}/racks/${rackId}/activity-logs`, {
      params: {
        page: 1,
        limit: 999,
        tz: getTimezoneOffset(),
      },
    })
    .then((response: AxiosResponse<Response<ActivityLog>>) => response.data)
    .then((response: Response<ActivityLog>) => response.data);
};
const getRackActivityLogsList = (
  rackId: ID,
  day?: string,
  month?: string,
  page?: number,
): Promise<RacksActivityLogResponse> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .get(`${RACK_URL}/orgs/${organizationId}/racks/${rackId}/activity-logs/list`, {
      params: {
        page: page || 1,
        limit: 10,
        day,
        month,
        tz: getTimezoneOffset(),
      },
    })
    .then(
      (
        d: AxiosResponse<{
          data: {
            items: Array<LogItemDetail>;
            total: number;
          };
        }>,
      ) => {
        return {
          data: d.data.data,
        };
      },
    );
};
const getActivityLogDetail =
  (callback: () => void) =>
  (query: string, rackId: ID, moreParam?: string): Promise<ActivityLogDetailQueryResponse> => {
    const organizationId = useOrganizationStore.getState().state.organization?.id;

    return axios
      .get(
        `${RACK_URL}/orgs/${organizationId}/racks/${rackId}/activity-logs/${moreParam}/details?${query}`,
      )
      .then(
        (
          d: AxiosResponse<{
            data: {
              items: Array<LogItemDetail>;
              total: number;
            };
          }>,
        ) => {
          return {
            data: d.data.data,
          };
        },
      )
      .finally(callback);
  };

export {
  createRack,
  deleteRack,
  getActivityLogDetail,
  getHistory,
  getRack,
  getRackActivityLogsList,
  getRacks,
  getRacks2,
  synchronizedRack,
  updateBinInRack,
  updateRack,
};

export const getShortForStockRuns = (
  query: string,
  _id: ID,
  moreParam?: string,
): Promise<StockRunsQueryResponse> => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .get(`${API_URL}/api/orgs/${organizationId}/stock-run/short-for?${query}${moreParam || ''}`)
    .then((res: AxiosResponse<{ data: StockRun[] }>) => ({
      data: {
        items: res.data.data.map((el) => ({ ...el, statusName: StatusStockUp[el.status] })),
        total: res.data.data.length,
      },
    }));
};

export const addItemToStockRun = (stockRunId: ID, body: { itemId: ID; binId: ID }) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios
    .post(`${API_URL}/api/orgs/${organizationId}/stock-run/${stockRunId}/short-for`, body)
    .then((res: AxiosResponse<{ data: { items: StockRun[]; total: number } }>) => ({
      data: res.data.data,
    }));
};

export const getListBinsAlreadyAssigned = async (query: string, rackId: ID) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  return axios.get(`${RACK_URL_V2}/orgs/${organizationId}/racks-v2/${rackId}/bins?${query}`).then(
    (
      d: AxiosResponse<{
        data: {
          items: Array<Rack>;
          total: number;
        };
      }>,
    ) => {
      return {
        data: d.data.data,
      };
    },
  );
};

export const updateBaseWeight = async (
  sensorId: ID,
  request: { baseWeight: number },
): Promise<Sensor | undefined> => {
  try {
    const organizationId = useOrganizationStore.getState().state.organization?.id;

    const res = await axios.patch<ApiResponse<Sensor>>(
      `${RACK_URL}/orgs/${organizationId}/sensors/${sensorId}/base-weight`,
      request,
    );

    if (!res.data.success) {
      toast.error(res.data.message || 'Failed to update base weight');
      return undefined;
    }

    toast.success('Base weight updated successfully');
    return res.data.data as Sensor;
  } catch (error) {
    getResponseError(error);
    return undefined;
  }
};
